import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function CalendarioFechamento() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [calendario, setCalendario] = useState(null);
  const [periodicidade, setPeriodicidade] = useState("mensal");
  const [periodos, setPeriodos] = useState([{ dia_inicio: 1, dia_fim: 31, dia_pagamento: 5 }]);
  const [formaPagamento, setFormaPagamento] = useState("PIX");
  const [observacoes, setObservacoes] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  useEffect(() => {
    if (!empresaId) return;
    api.get(`/empresas/${empresaId}/calendario-fechamento`).then(r => {
      if (r.data && r.data.status !== "nao_cadastrado") {
        setCalendario(r.data);
        setPeriodicidade(r.data.periodicidade);
        setPeriodos(r.data.periodos || []);
        setFormaPagamento(r.data.forma_pagamento || "PIX");
        setObservacoes(r.data.observacoes || "");
      } else {
        setCalendario(null);
      }
    });
  }, [empresaId]);

  // Reseta períodos quando muda periodicidade
  useEffect(() => {
    if (calendario && periodicidade === calendario.periodicidade) return;
    if (periodicidade === "semanal") {
      setPeriodos([{ dia_inicio_semana: "segunda", dia_fim_semana: "domingo", dia_pagamento_apos: 3 }]);
    } else if (periodicidade === "quinzenal") {
      setPeriodos([
        { dia_inicio: 1, dia_fim: 15, dia_pagamento: 18 },
        { dia_inicio: 16, dia_fim: 31, dia_pagamento: 3 },
      ]);
    } else {
      setPeriodos([{ dia_inicio: 1, dia_fim: 31, dia_pagamento: 5 }]);
    }
  }, [periodicidade]); // eslint-disable-line

  const updatePeriodo = (i, patch) => {
    setPeriodos(prev => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  };

  const salvar = async () => {
    if (!empresaId) { toast.error("Selecione empresa"); return; }
    setSalvando(true);
    try {
      await api.post(`/empresas/${empresaId}/calendario-fechamento`, {
        periodicidade, periodos, forma_pagamento: formaPagamento, observacoes,
      });
      toast.success("Calendário salvo");
      const r = await api.get(`/empresas/${empresaId}/calendario-fechamento`);
      setCalendario(r.data);
      if (user?.role === "empresa") {
        try {
          const s = (await api.get("/empresa/condicoes-status")).data;
          if (!s.tabela_ok) {
            toast.info("Próximo passo: Tabela de Valores");
            navigate("/admin/valores-servicos");
          } else if (!s.fiscal_ok) {
            toast.info("Próximo passo: Nota Fiscal e dados fiscais");
            navigate("/admin/config-fiscal");
          } else {
            toast.info("Tudo definido — publique as condições para liberar o aceite de Montadores e Montadoras");
            navigate("/admin/condicoes");
          }
        } catch { /* segue na página */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <div className="label-cap">Configuração · Empresa</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Calendário de Fechamento</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Configure quando e como os pagamentos aos montadores serão fechados. O sistema gera fechamentos automáticos.
        </p>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Label className="label-cap">Empresa</Label>
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="mt-2 max-w-md" data-testid="cal-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {empresaId && (
        <div className="border border-border bg-white p-6 space-y-5">
          <div>
            <Label className="label-cap">Periodicidade</Label>
            <Select value={periodicidade} onValueChange={setPeriodicidade}>
              <SelectTrigger className="mt-2 max-w-xs" data-testid="cal-periodicidade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semanal">Semanal</SelectItem>
                <SelectItem value="quinzenal">Quinzenal (2 ciclos/mês)</SelectItem>
                <SelectItem value="mensal">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Renderiza períodos */}
          <div>
            <Label className="label-cap mb-2 block">Períodos de fechamento</Label>
            <div className="border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] border-b border-border">
                  <tr>
                    <th className="text-left p-3 label-cap font-medium">Ciclo</th>
                    <th className="text-left p-3 label-cap font-medium">Início</th>
                    <th className="text-left p-3 label-cap font-medium">Fim</th>
                    <th className="text-left p-3 label-cap font-medium">Pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {periodos.map((p, i) => (
                    <tr key={i} className="border-b border-border last:border-0" data-testid={`cal-row-${i}`}>
                      <td className="p-3 font-mono">{i + 1}</td>
                      {periodicidade === "semanal" ? (
                        <>
                          <td className="p-3">
                            <Select value={p.dia_inicio_semana || "segunda"} onValueChange={(v) => updatePeriodo(i, { dia_inicio_semana: v })}>
                              <SelectTrigger className="h-9" data-testid={`cal-inicio-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {["segunda","terca","quarta","quinta","sexta","sabado","domingo"].map(d =>
                                  <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Select value={p.dia_fim_semana || "domingo"} onValueChange={(v) => updatePeriodo(i, { dia_fim_semana: v })}>
                              <SelectTrigger className="h-9" data-testid={`cal-fim-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {["segunda","terca","quarta","quinta","sexta","sabado","domingo"].map(d =>
                                  <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs">D+</span>
                              <Input type="number" min="0" max="30" value={p.dia_pagamento_apos || 3}
                                onChange={(e) => updatePeriodo(i, { dia_pagamento_apos: parseInt(e.target.value) || 0 })}
                                className="h-9 w-20" data-testid={`cal-pgto-${i}`} />
                              <span className="text-xs">dias após fim</span>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs">Dia</span>
                              <Input type="number" min="1" max="31" value={p.dia_inicio || 1}
                                onChange={(e) => updatePeriodo(i, { dia_inicio: parseInt(e.target.value) || 1 })}
                                className="h-9 w-20" data-testid={`cal-inicio-${i}`} />
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs">Dia</span>
                              <Input type="number" min="1" max="31" value={p.dia_fim || 31}
                                onChange={(e) => updatePeriodo(i, { dia_fim: parseInt(e.target.value) || 31 })}
                                className="h-9 w-20" data-testid={`cal-fim-${i}`} />
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs">Dia</span>
                              <Input type="number" min="1" max="31" value={p.dia_pagamento || 5}
                                onChange={(e) => updatePeriodo(i, { dia_pagamento: parseInt(e.target.value) || 5 })}
                                className="h-9 w-20" data-testid={`cal-pgto-${i}`} />
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="label-cap">Forma de pagamento</Label>
              <Select value={formaPagamento} onValueChange={setFormaPagamento}>
                <SelectTrigger className="mt-2" data-testid="cal-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="TED">TED</SelectItem>
                  <SelectItem value="DOC">DOC</SelectItem>
                  <SelectItem value="Boleto">Boleto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-cap">Observações</Label>
            <Textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
              rows={2} className="mt-2 text-sm" data-testid="cal-obs" />
          </div>

          <div className="pt-4 border-t border-border flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {calendario ? `Última atualização: ${new Date(calendario.criado_em).toLocaleString("pt-BR")}` : "Sem calendário ativo"}
            </div>
            <Button onClick={salvar} disabled={salvando}
              className="btn-sharp brand-bg text-white" data-testid="cal-salvar">
              {salvando ? "Salvando…" : calendario ? "Atualizar calendário" : "Salvar calendário"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
